/**
 * Requests for the Create Multiple Users wizard (PD-4066).
 *
 * Sibling of createUserService, which serves the single-user wizard. Everything
 * talks to Management::BulkUserImportsController.
 */

import { BULK_IMPORT_COMPLETED } from '../userEventKinds';

const csrfToken = () =>
  document.querySelector('[name="csrf-token"]')?.getAttribute('content') ?? '';

export const TEMPLATE_PATH = '/management/bulk_user_imports/template.csv';

/**
 * Uploads the CSV and asks for it to be processed.
 *
 * Resolves with the import on 202 — accepted, not finished. On 422 it throws an
 * error carrying the model's field errors, which is how the upload step learns
 * that the file was too big or was not a CSV; anything else throws bare.
 */
export const createBulkUserImport = async (file) => {
  const body = new FormData();
  body.append('file', file);

  const response = await fetch('/management/bulk_user_imports.json', {
    method: 'POST',
    headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
    body,
  });

  if (response.ok) {
    const data = await response.json();
    return data.import;
  }

  const error = new Error(`HTTP error! status: ${response.status}`);

  if (response.status === 422) {
    // A 422 body is ours, but do not let a malformed one mask the failure.
    error.fieldErrors = await response
      .json()
      .then((data) => data.errors ?? {}, () => ({}));
  }

  throw error;
};

/** Where an import has got to. Polled while it runs. */
export const fetchBulkUserImport = async (id) => {
  const response = await fetch(`/management/bulk_user_imports/${id}.json`, {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  return data.import;
};

/**
 * The current user's uncollected events.
 *
 * This is what makes the design's toasts survive the admin closing the wizard:
 * the outcome is a row on the server, not a message held in this page's memory.
 */
export const fetchUserEvents = async (kinds = []) => {
  const params = new URLSearchParams();
  kinds.forEach((kind) => params.append('kinds[]', kind));

  const suffix = params.toString() ? `?${params}` : '';
  const response = await fetch(`/management/user_events.json${suffix}`, {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  return data.events || [];
};

/**
 * Marks events consumed.
 *
 * Called only once the toast has actually been raised, so that a failed request
 * leaves the event to be collected again rather than losing the notice.
 */
export const markUserEventsRead = async (ids) => {
  if (!ids.length) return;

  await fetch('/management/user_events/mark_read.json', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    body: JSON.stringify({ ids }),
  });
};

/**
 * Consumes the completion event for one import, without raising its toast.
 *
 * The standard is that an admin sees the outcome once: in the wizard if they
 * stayed with it, as a toast if they closed it. So the wizard spends its own
 * event when it shows the Summary step, and the page's toast never fires for an
 * import that was already reported on screen.
 *
 * Scoped to `importId` rather than clearing everything unread, because a
 * *different* import may have finished while this one was open and that one's
 * toast is still owed.
 *
 * Never rejects: failing to consume costs a duplicate toast, which is not worth
 * failing the summary over.
 */
export const consumeImportEvents = async (importId) => {
  try {
    const events = await fetchUserEvents([BULK_IMPORT_COMPLETED]);
    const ids = events
      .filter((event) => String(event.subjectId) === String(importId))
      .map((event) => event.id);

    await markUserEventsRead(ids);
  } catch {
    // See above.
  }
};
